import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const langRef = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { lang, setLang, t } = useLanguage();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === lang);

  const initials = user ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() : '';

  // Dropdown menus config
  const passengersMenu = [
    { label: { ru: 'Путешествие с домашними животными', en: 'Traveling with pets', lv: 'Ceļošana ar mājdzīvniekiem', lt: 'Kelionė su augintiniais', pl: 'Podróż ze zwierzętami' }, slug: 'faq-pets' },
    { label: { ru: 'Путешествие с несовершеннолетними', en: 'Traveling with minors', lv: 'Ceļošana ar nepilngadīgajiem', lt: 'Kelionė su nepilnamečiais', pl: 'Podróż z małoletnimi' }, slug: 'faq-minors' },
    { label: { ru: 'Как путешествовать с велосипедом?', en: 'How to travel with a bicycle?', lv: 'Kā ceļot ar velosipēdu?', lt: 'Kaip keliauti su dviračiu?', pl: 'Jak podróżować z rowerem?' }, slug: 'faq-bicycle' },
    { label: { ru: 'Часто задаваемые вопросы', en: 'FAQ', lv: 'Biežāk uzdotie jautājumi', lt: 'DUK', pl: 'FAQ' }, slug: 'faq' },
  ];

  const aboutMenu = [
    { label: { ru: 'О Lux Express', en: 'About Lux Express', lv: 'Par Lux Express', lt: 'Apie Lux Express', pl: 'O Lux Express' }, slug: 'about' },
    { label: { ru: 'Контакты', en: 'Contacts', lv: 'Kontakti', lt: 'Kontaktai', pl: 'Kontakt' }, slug: 'contacts' },
    { label: { ru: 'Карьера', en: 'Career', lv: 'Karjera', lt: 'Karjera', pl: 'Kariera' }, slug: 'career' },
    { label: { ru: 'Бизнес-сервис', en: 'Business service', lv: 'Biznesa serviss', lt: 'Verslo paslauga', pl: 'Usługi biznesowe' }, slug: 'business' },
  ];

  const infoMenu = [
    { label: { ru: 'Политика конфиденциальности', en: 'Privacy policy', lv: 'Privātuma politika', lt: 'Privatumo politika', pl: 'Polityka prywatności' }, slug: 'privacy' },
    { label: { ru: 'Правила продажи билетов', en: 'Ticket sales rules', lv: 'Biļešu tirdzniecības noteikumi', lt: 'Bilietų pardavimo taisyklės', pl: 'Zasady sprzedaży biletów' }, slug: 'terms' },
    { label: { ru: 'Таможенная служба', en: 'Customs', lv: 'Muita', lt: 'Muitinė', pl: 'Cło' }, slug: 'customs' },
    { label: { ru: 'Правила перевозки', en: 'Transport rules', lv: 'Pārvadāšanas noteikumi', lt: 'Vežimo taisyklės', pl: 'Zasady przewozu' }, slug: 'transport' },
  ];

  const DropdownMenu = ({ id, items }) => (
    openDropdown === id && (
      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border py-1 min-w-[260px] z-50">
        {items.map((item) => (
          <button
            key={item.slug}
            onClick={() => { navigate(`/info/${item.slug}`); setOpenDropdown(null); }}
            className="w-full text-left px-4 py-2.5 text-sm text-[#484c55] hover:bg-[#f0f0f1] hover:text-[#c30909] transition-colors"
          >
            {item.label[lang] || item.label.en}
          </button>
        ))}
      </div>
    )
  );

  const navTextClass = `text-[14px] font-semibold tracking-wide uppercase transition-colors hover:text-[#c30909] ${isHome ? 'text-white' : 'text-[#222]'}`;

  return (
    <>
      <header className={`w-full z-50 ${isHome ? 'absolute top-0 left-0' : 'bg-white shadow-sm'}`}>
        <div className="w-full px-3 md:pl-8 md:pr-8 flex items-center justify-between h-[56px] md:h-[72px]">
          <Link to="/" className="flex-shrink-0 mr-8 flex items-baseline gap-[1px] overflow-visible">
            <span className="leading-none inline-flex overflow-visible"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', transform: 'scaleX(0.6) skewX(-8deg)', transformOrigin: 'left bottom', paddingRight: '4px' }}>
              <span className={`text-[34px] md:text-[40px]`}
                style={{ letterSpacing: '-0.08em', color: isHome ? '#e8d9a8' : '#c30909' }}>L</span>
              <span className={`text-[34px] md:text-[40px]`}
                style={{ letterSpacing: '-0.06em', color: isHome ? '#e8d9a8' : '#c30909' }}>U</span>
              <span className={`text-[34px] md:text-[40px]`} style={{
                background: isHome
                  ? 'linear-gradient(90deg, #e8d9a8 35%, #c30909 100%)'
                  : 'linear-gradient(90deg, #c30909 35%, #8b0000 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>X</span>
            </span>
            <span
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontStyle: 'italic', fontSize: '21px', letterSpacing: '-0.03em', marginLeft: '-28px', marginBottom: '1px', lineHeight: 1, alignSelf: 'flex-end', color: isHome ? '#d0d0d0' : '#888', transform: 'scaleX(0.65) skewX(-6deg)', transformOrigin: 'left bottom' }}>Express</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-2 xl:gap-5" ref={dropdownRef}>
            {/* LUX EXPRESS SUPERIOR */}
            <button onClick={() => navigate('/info/superior')} className={navTextClass}>
              LUX EXPRESS SUPERIOR
            </button>

            {/* МАРШРУТЫ */}
            <button onClick={() => navigate('/info/routes')} className={navTextClass}>
              {t('nav.routes')}
            </button>

            {/* МОИ БРОНИРОВАНИЯ */}
            <button onClick={() => user ? navigate('/account') : setAuthOpen(true)} className={navTextClass}>
              {t('nav.myBookings')}
            </button>

            {/* ПАССАЖИРАМ ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'passengers' ? null : 'passengers')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.passengers')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'passengers' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="passengers" items={passengersMenu} />
            </div>

            {/* О НАС ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'about' ? null : 'about')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.about')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="about" items={aboutMenu} />
            </div>

            {/* ИНФОРМАЦИЯ ▾ */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'info' ? null : 'info')}
                className={`${navTextClass} flex items-center gap-0.5`}
              >
                {t('nav.info')}
                <ChevronDown size={10} className={`transition-transform ${openDropdown === 'info' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu id="info" items={infoMenu} />
            </div>
          </nav>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Auth */}
            {user ? (
              <button
                onClick={() => navigate('/account')}
                className={`hidden md:flex items-center gap-2 text-xs font-semibold transition-colors ${isHome ? 'text-white' : 'text-[#484c55]'}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#c9a96e] flex items-center justify-center text-white text-xs font-bold">
                  {initials}
                </div>
              </button>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                className={`hidden md:flex items-center gap-1 text-[14px] font-semibold uppercase tracking-wide transition-colors hover:text-[#c30909] ${
                  isHome ? 'text-white' : 'text-[#222]'
                }`}
              >
                {t('auth.loginBtn')}
              </button>
            )}

            {/* Language */}
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1 cursor-pointer">
                <Globe size={16} className={isHome ? 'text-white' : 'text-[#484c55]'} />
                <span className={`text-xs font-semibold ${isHome ? 'text-white' : 'text-[#484c55]'}`}>{currentLang?.label}</span>
                <ChevronDown size={12} className={`transition-transform ${langOpen ? 'rotate-180' : ''} ${isHome ? 'text-white/60' : 'text-[#484c55]/40'}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border py-1 min-w-[160px] z-50">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#f0f0f1] transition-colors flex items-center justify-between ${lang === l.code ? 'text-[#c30909] font-semibold' : 'text-[#484c55]'}`}
                    >
                      <span>{l.name}</span>
                      <span className="text-xs text-[#484c55]/40 font-medium">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={24} className={isHome ? 'text-white' : 'text-[#484c55]'} /> : <Menu size={24} className={isHome ? 'text-white' : 'text-[#484c55]'} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white shadow-lg border-t max-h-[80vh] overflow-auto">
            <nav className="flex flex-col p-4 gap-1">
              <button className="text-sm font-semibold text-[#484c55] py-2 text-left" onClick={() => { navigate('/info/superior'); setMobileOpen(false); }}>
                LUX EXPRESS SUPERIOR
              </button>
              <button className="text-sm font-semibold text-[#484c55] py-2 text-left" onClick={() => { navigate('/info/routes'); setMobileOpen(false); }}>
                {t('nav.routes')}
              </button>
              <button className="text-sm font-semibold text-[#484c55] py-2 text-left" onClick={() => { user ? navigate('/account') : setAuthOpen(true); setMobileOpen(false); }}>
                {t('nav.myBookings')}
              </button>

              {/* Passengers submenu */}
              <div className="border-t mt-1 pt-2">
                <p className="text-xs font-semibold text-[#484c55]/50 uppercase tracking-wider mb-1">{t('nav.passengers')}</p>
                {passengersMenu.map((item) => (
                  <button key={item.slug} className="text-sm text-[#484c55] py-1.5 text-left w-full hover:text-[#c30909]"
                    onClick={() => { navigate(`/info/${item.slug}`); setMobileOpen(false); }}>
                    {item.label[lang] || item.label.en}
                  </button>
                ))}
              </div>

              {/* About submenu */}
              <div className="border-t mt-1 pt-2">
                <p className="text-xs font-semibold text-[#484c55]/50 uppercase tracking-wider mb-1">{t('nav.about')}</p>
                {aboutMenu.map((item) => (
                  <button key={item.slug} className="text-sm text-[#484c55] py-1.5 text-left w-full hover:text-[#c30909]"
                    onClick={() => { navigate(`/info/${item.slug}`); setMobileOpen(false); }}>
                    {item.label[lang] || item.label.en}
                  </button>
                ))}
              </div>

              {/* Info submenu */}
              <div className="border-t mt-1 pt-2">
                <p className="text-xs font-semibold text-[#484c55]/50 uppercase tracking-wider mb-1">{t('nav.info')}</p>
                {infoMenu.map((item) => (
                  <button key={item.slug} className="text-sm text-[#484c55] py-1.5 text-left w-full hover:text-[#c30909]"
                    onClick={() => { navigate(`/info/${item.slug}`); setMobileOpen(false); }}>
                    {item.label[lang] || item.label.en}
                  </button>
                ))}
              </div>

              {/* Language */}
              <div className="flex flex-wrap gap-2 py-2 border-t mt-2 pt-3">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setMobileOpen(false); }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${lang === l.code ? 'bg-[#c30909] text-white border-[#c30909]' : 'text-[#484c55] border-[#484c55]/30 hover:border-[#c30909]/50'}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {/* Auth */}
              {user ? (
                <div className="border-t pt-3 space-y-2">
                  <button onClick={() => { navigate('/account'); setMobileOpen(false); }} className="text-sm font-semibold text-[#c30909] py-1">{t('account.myAccount')}</button>
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="text-sm text-[#484c55]/60 py-1">{t('auth.logout')}</button>
                </div>
              ) : (
                <button onClick={() => { setAuthOpen(true); setMobileOpen(false); }} className="text-sm font-semibold text-[#c30909] border border-[#c30909] rounded-full px-4 py-2 mt-2">
                  {t('auth.loginBtn')}
                </button>
              )}
            </nav>
          </div>
        )}
      </header>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};

export default Header;
